import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { Card, CardContent, CardHeader, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { memo, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useDeepMemo, useSetState, useUniqueEffect } from '../../hooks';
import connectionService from '../../services/Connection';
import util from '../../util/index';
import { usePrerequisites } from '../../contexts/Prerequisites';
import ActionIconButton from './ActionIconButton';
import StackedChart from './StackedChart';

const ChartComponent = ({
	onRemoveChart,
	onShowSettings,
	config,
	equipmentId,
	advancedSearch,
	tableFilters,
	hideLegend,
	labelProps,
	loading,
	fetchConfigData,
	index,
	label,
	data
}) => {
	const [{ strings }] = usePrerequisites();
	const [state, setState] = useSetState({
		yAxisLabel: strings.CycleCount,
		xAxisLabel:
			config.selectedChartType === 'Stacked' || config.selectedChartType === 'StackedChart'
				? strings[config?.stackedChartGroup1] || config?.stackedChartGroup1 || ''
				: strings[config?.selectedChartGroup] || config?.selectedChartGroup || ''
	});

	const handleOnShowSettings = () => {
		if (typeof onShowSettings === 'function') {
			onShowSettings(config);
		}
	};

	const handleOnRemoveChart = () => {
		if (typeof onRemoveChart === 'function') {
			onRemoveChart(config);
		}
	};

	const handleFetchConfigData = useCallback(() => {
		if (typeof fetchConfigData === 'function') {
			fetchConfigData(config, index)
		}
	}, [fetchConfigData, index])

	const reqBody = useDeepMemo(() => {
		let selectedValue = config.selectedChartGroup;
		if (config.selectedChartType === 'Stacked') {
			selectedValue = `${config.stackedChartGroup1},${config.stackedChartGroup2}`;
		}
		let formData = new FormData();
		const chartType = config.selectedChartType ? config.selectedChartType : 'pie';
		formData.append('chartType', chartType);
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'LoadChart');
		formData.append('equipmentId', equipmentId || 0);
		formData.append('groupBy', selectedValue);

		if (advancedSearch) {
			formData.append('AdvanceSearch', advancedSearch);
		}
		let dateTimeFormat = util.getDateTimeFormat();
		const isForDate = config.stackedChartGroup1 === 'Day' || config.stackedChartGroup2 === 'Day';
		const filters = util.getStartTimeAndFilter(tableFilters, isForDate);
		tableFilters = tableFilters && !tableFilters.cancelable ? tableFilters : config.tableFilters;
		formData.append('selectedCycleDate', dayjs(filters.filterStartDate).format(dateTimeFormat.date));
		if (filters.filters) {
			formData.append('filter', filters.filters);
		}

		return formData;
	}, [config, advancedSearch, equipmentId, tableFilters]);

	const fetchData = useCallback(() => {
		if (config.selectedChartType === 'Stacked') {
			if (!config.stackedChartGroup1) {
				Swal.fire({ titleText: strings.Alert, text: strings.GroupByValue, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				return;
			}
			if (!config.stackedChartGroup2) {
				Swal.fire({ titleText: strings.Alert, text: strings.ThenSecondLevel, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				return;
			}
		}
		setState({ loading: true });
		fetch(connectionService.CycleList, {
			body: reqBody,
			method: 'POST',
			credentials: 'include'
		})
			.then((res) => res.json())
			.then(({ data }) => {
				const total =
					['Pie', 'PieChart'].includes(config?.selectedChartType) && Array.isArray(data)
						? data.reduce((acc, entry) => acc + (entry.value || 0), 0)
						: 0;
				setState({ data: data || null, label: `${total} ${strings.CycleList}` });
			})
			.catch((err) => Swal.fire({ titleText: strings.ErrorTitle, text: strings.Status_503, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false }))
			.finally(() => setState({ loading: false }));
	}, [reqBody]);

	useUniqueEffect(() => {
		handleFetchConfigData();
	}, [config]);

	return (
		<Card sx={{ width: '100%' }} elevation={0} variant="outlined">
			<CardHeader
				action={
					<Stack direction="row" spacing={1} className="print-hide">
						<ActionIconButton
							color="primary"
							size="small"
							className="no-print"
							variant="outlined"
							onClick={handleOnShowSettings}
							title={strings.Settings}
						>
							<SettingsIcon fontSize="inherit" />
						</ActionIconButton>
						<ActionIconButton
							size="small"
							color="error"
							className="no-print"
							variant="outlined"
							onClick={handleOnRemoveChart}
							title={strings.Close}
						>
							<CloseIcon fontSize="inherit" />
						</ActionIconButton>
					</Stack>
				}
				title={strings[config.chartTitle] || config.chartTitle}
				titleTypographyProps={{ variant: 'subtitle2' }}
			/>
			<CardContent>
				{!loading && !!data ? (
					<StackedChart
						data={data}
						config={config}
						hideLegend={hideLegend}
						yAxisLabel={state.yAxisLabel}
						xAxisLabel={state.xAxisLabel}
						label={label}
						labelProps={labelProps}
					/>
				) : (
					<Stack justifyContent="center" alignItems="center" spacing={12}>
						{!loading && !data && (
							<Typography
								variant="h5"
								color="textSecondary"
								align="center"
								sx={{
									paddingTop: (theme) => theme.spacing(5),
									paddingBottom: (theme) => theme.spacing(5)
								}}
							>
								{strings.NoRecordFound}
							</Typography>
						)}
						{loading && <CircularProgress size={24} />}
					</Stack>
				)}
			</CardContent>
		</Card>
	);
};

export default memo(ChartComponent);
